/**
 * Unified event pipeline — sends every tracked event to BOTH:
 *   1. Google Analytics 4 via `window.gtag('event', name, params)`
 *   2. SlushBook's own DB via `POST /api/analytics/event`
 *
 * Single call site for both pipelines so we never drift apart and never
 * write the same trackEvent code twice.
 *
 * Why two pipelines:
 *   • GA4 powers Path/Funnel Exploration, attribution to Facebook/UTM,
 *     and Pro-conversion analysis.
 *   • The internal DB is the source of truth for admin-only views
 *     (welcome-access funnel, empty-search analytics, future custom
 *     dashboards) and survives even if GA4 is blocked by an ad-blocker.
 *
 * GA4 doesn't allow dots in event names — we replace `.` with `_`
 * automatically when forwarding (`welcome_access.granted` →
 * `welcome_access_granted`). The DB keeps the original dotted name so
 * existing analytics_events rows stay queryable.
 *
 * Never throws — must never block the UI on storage failures.
 */

import axios from 'axios';
import { API } from '../App';

// Marker so the production console can verify whether THIS analytics
// pipeline (the GA4-forwarding one) is actually shipped. Paste in DevTools:
//   window.__SLUSHBOOK_ANALYTICS__
// — `undefined` → bundle is stale (redeploy needed); object → pipeline is live.
const ANALYTICS_VERSION = '2026-02-iter1';
if (typeof window !== 'undefined') {
  window.__SLUSHBOOK_ANALYTICS__ = {
    version: ANALYTICS_VERSION,
    description: 'GA4 + DB unified event pipeline (iteration 1).',
  };
}

// ────────────────────── Event names ──────────────────────
// Constants instead of magic strings so a typo at the call site fails
// the build, not silently the analytics. Names follow GA4 conventions
// (snake_case, ≤40 chars).
export const ANALYTICS_EVENTS = Object.freeze({
  // ── Auth ──
  SIGN_UP: 'sign_up',                                    // GA4 standard
  LOGIN: 'login',                                        // GA4 standard
  LOGOUT: 'logout',

  // ── Onboarding ──
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  INTEREST_SELECTED: 'interest_selected',
  MACHINE_SELECTED: 'machine_selected',

  // ── Recipes ──
  RECIPE_OPEN: 'recipe_open',                            // mirrors GA4 view_item
  RECIPE_SCALED: 'recipe_scaled',

  // ── Match / Pantry ──
  MATCH_SEARCH_RUN: 'match_search_run',
  INGREDIENT_MATCH: 'ingredient_match',

  // ── Pro / Checkout ──
  VIEW_PRICING: 'view_pricing',
  BEGIN_CHECKOUT: 'begin_checkout',                      // GA4 standard
  PURCHASE: 'purchase',                                  // GA4 standard

  // ── Welcome-access funnel (already in production) ──
  WELCOME_ACCESS_GRANTED: 'welcome_access.granted',
  WELCOME_ACCESS_RECIPE_OPENED: 'welcome_access.recipe_opened',
  WELCOME_ACCESS_UPGRADE_CLICKED: 'welcome_access.upgrade_clicked',

  // ── Guides — reactions (likes) ──
  GUIDE_LIKE: 'guide_like',
  GUIDE_UNLIKE: 'guide_unlike',
});

/** GA4 disallows `.` in event names. Translate at the boundary. */
const toGa4EventName = (name) => name.replace(/\./g, '_');

/** Strip nulls + functions so GA4 doesn't reject the params payload. */
const sanitizeParams = (props) => {
  const out = {};
  for (const [k, v] of Object.entries(props || {})) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'function') continue;
    if (Array.isArray(v)) {
      // GA4 doesn't accept arrays as params — serialise as a CSV string.
      out[k] = v.filter((x) => x !== null && x !== undefined).join(',');
    } else if (typeof v === 'object') {
      out[k] = JSON.stringify(v).slice(0, 100);
    } else {
      // GA4 caps string params at 100 chars.
      out[k] = typeof v === 'string' ? v.slice(0, 100) : v;
    }
  }
  return out;
};

/**
 * Set persistent user properties on the GA4 session. Call on login, on
 * subscription change, on machine pick — anywhere a long-lived user
 * dimension changes. These power Path/Funnel filtering in GA4.
 *
 * Properties we set:
 *   • user_role        ("guest" | "user" | "pro" | "editor" | "admin")
 *   • subscription_type ("free" | "pro_monthly" | "pro_yearly")
 *   • machine_name      (e.g. "Wilfa FS-200")
 *   • primary_interest  (top onboarding interest)
 */
export function setUserProperties(properties = {}) {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const clean = sanitizeParams(properties);
    if (Object.keys(clean).length === 0) return;
    window.gtag('set', 'user_properties', clean);
  } catch (_e) {
    /* never break UI for analytics */
  }
}

/** Set the long-lived user identifier (used to join sessions across devices). */
export function setUserId(userId) {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    if (!userId) {
      window.gtag('set', { user_id: undefined });
      return;
    }
    window.gtag('set', { user_id: String(userId) });
  } catch (_e) {
    /* swallow */
  }
}

/**
 * Track a user action. Fires the same event into BOTH GA4 and the
 * internal DB. Fire-and-forget — never awaits.
 */
export function trackEvent(event, props = {}) {
  if (!event) return;
  const params = sanitizeParams(props);

  // Production-visible breadcrumb. `console.info` shows up in DevTools at
  // default log levels so users can immediately verify in prod that
  // trackEvent actually fired — without needing extra dev tooling.
  // If this is missing from the console, it means the new bundle isn't
  // live yet (CDN cache or pre-iteration1 deploy).
  try {
    // eslint-disable-next-line no-console
    console.info(
      '[SlushBook analytics]',
      event,
      params,
      'gtag=', typeof window !== 'undefined' && typeof window.gtag === 'function',
    );
  } catch (_e) {
    /* swallow */
  }

  // 1) GA4
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', toGa4EventName(event), params);
    }
  } catch (_e) {
    /* swallow */
  }

  // 2) Internal DB (best-effort; whitelist enforced server-side)
  try {
    axios
      .post(`${API}/analytics/event`, { event, props: params })
      .catch((err) => {
        console.debug('[analytics] event failed', event, err?.response?.status);
      });
  } catch (_e) {
    /* swallow */
  }
}
